import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        // Filtrer uniquement les articles publiés
        const publishedPosts = response.data.posts.filter(p => p.isPublished);
        setPosts(publishedPosts);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const categories = ['all', ...new Set(posts.map(p => p.category).filter(Boolean))];
  
  const filteredPosts = selectedCategory === 'all'
    ? posts
    : posts.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C9A96E] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero */}
      <section className="relative py-24 bg-[#2C1810]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Actualités</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-4 mb-6">
            Blog & Conseils
          </h1>
          <div className="w-20 h-1 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B] mx-auto mb-6" />
          <p className="text-[#D4C4B5] text-lg max-w-2xl mx-auto">
            Découvrez nos conseils beauté, nos actualités et les tendances coiffure.
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Category Filter */}
          {categories.length > 1 && (
            <div className="flex justify-center gap-4 mb-12 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-6 py-2 rounded-full font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-[#C9A96E] text-white'
                      : 'bg-white text-[#2C1810] hover:bg-[#F5F0EB]'
                  }`}
                >
                  {cat === 'all' ? 'Tous' : cat}
                </button>
              ))}
            </div>
          )}

          {/* Posts Grid */}
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#5D4037]">Aucun article disponible pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article key={post.id} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all group">
                  <div className="aspect-video bg-[#F5F0EB] relative overflow-hidden">
                    {post.image ? (
                      <img
                        src={post.image.startsWith('/uploads') ? `${BACKEND_URL}${post.image}` : post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Calendar size={48} className="text-[#D4C4B5]" />
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-[#A67B5B] mb-3">
                      {post.author && (
                        <div className="flex items-center gap-1">
                          <User size={14} />
                          <span>{post.author}</span>
                        </div>
                      )}
                      {post.publishedAt && (
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>{format(new Date(post.publishedAt), 'dd MMM yyyy', { locale: fr })}</span>
                        </div>
                      )}
                    </div>
                    {post.category && (
                      <span className="inline-block px-3 py-1 bg-[#F5F0EB] text-[#C9A96E] text-xs font-medium rounded-full mb-3">
                        {post.category}
                      </span>
                    )}
                    <h2 className="text-xl font-serif font-bold text-[#2C1810] mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-[#5D4037] mb-4 line-clamp-3">
                      {post.excerpt || post.content?.substring(0, 150) + '...'}
                    </p>
                    <Link
                      to={`/blog/${post.slug || post.id}`}
                      className="inline-flex items-center gap-2 text-[#C9A96E] font-medium hover:gap-3 transition-all"
                    >
                      Lire la suite
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
